import { inject } from '@angular/core';
import { CanMatchFn, Router, Routes, UrlMatchResult, UrlSegment } from '@angular/router';
import { langGuard } from './@guards/lang-guard';
import { MainLayoutComponent } from './main-layout/main-layout.component';
import { NewsService } from './@services/news.service';

const allowedLangs = ['id', 'en'];
const projectListSlug = 'proyek-rumah';
const knownRouteRoots = new Set([
  'home',
  'office',
  'property',
  'project',
  'market',
  'about-ljhrealty',
  'tentang-kami',
  'testimony',
  'outlets',
  'daftar-outlet',
  'legal',
  'kpr-simulate',
  'simulasi-kpr',
  'article',
  'info-properti',
  'partnership-opportunity',
  'peluang-kemitraan',
  'property-consultant',
  'faq',
  'lidia',
  'contactus',
  projectListSlug,
]);

const normalizeRouteSegment = (segment: string | null | undefined) => String(segment ?? '')
  .toLowerCase()
  .trim()
  .replace(/^-+|-+$/g, '');

const isValidPropertyTypeSegment = (segment: string | null | undefined) => {
  const normalized = normalizeRouteSegment(segment);

  if (!normalized) {
    return false;
  }

  return normalized === 'property'
    || normalized === 'dijual'
    || normalized === 'disewa'
    || normalized.endsWith('-dijual')
    || normalized.endsWith('-disewa');
};

const isPropertyListingRootSegment = (segment: string | null | undefined) => {
  const normalized = normalizeRouteSegment(segment);

  if (!normalized) {
    return false;
  }

  return isValidPropertyTypeSegment(normalized) || !knownRouteRoots.has(normalized);
};

const legacyRouteMatcher = (segments: UrlSegment[]): UrlMatchResult | null => {
  if (segments.length === 0) {
    return null;
  }

  const [firstSegment] = segments;
  const looksLikeLangCode = /^[a-z]{2}$/i.test(firstSegment.path);

  if (allowedLangs.includes(firstSegment.path) || looksLikeLangCode) {
    return null;
  }

  if (
    !isPropertyListingRootSegment(firstSegment.path)
  ) {
    return null;
  }

  return { consumed: segments };
};

const propertyTypeCanMatch: CanMatchFn = (_route, segments) => {
  const [firstSegment] = segments;
  return isPropertyListingRootSegment(firstSegment?.path);
};

const looksLikeArticleSlug = (segment: string) => segment.split('-').filter(Boolean).length >= 4;

const articleRootDetailCanMatch: CanMatchFn = async (_route, segments) => {
  if (segments.length !== 1) {
    return false;
  }

  const [firstSegment] = segments;
  const normalizedSegment = normalizeRouteSegment(firstSegment?.path);
  if (
    !normalizedSegment ||
    knownRouteRoots.has(normalizedSegment) ||
    isValidPropertyTypeSegment(normalizedSegment)
  ) {
    return false;
  }

  const stateNewsId = Number(inject(Router).getCurrentNavigation()?.extras.state?.['newsId']);
  if (stateNewsId > 0) {
    return true;
  }

  if (looksLikeArticleSlug(normalizedSegment)) {
    return true;
  }

  try {
    return (await inject(NewsService).resolveNewsIdByTitle(normalizedSegment)) !== null;
  } catch {
    return false;
  }
};

const legacyOfficeRouteMatcher = (segments: UrlSegment[]): UrlMatchResult | null => {
  if (segments.length < 2 || segments.length > 4) {
    return null;
  }

  const [firstSegment, secondSegment, thirdSegment, fourthSegment] = segments;

  if (firstSegment.path !== 'office' || !secondSegment.path) {
    return null;
  }

  if (segments.length === 2) {
    return {
      consumed: segments,
      posParams: {
        company: secondSegment,
      }
    };
  }

  if (segments.length === 3 && thirdSegment.path) {
    return {
      consumed: segments,
      posParams: {
        company: secondSegment,
        agent_slug: thirdSegment,
      }
    };
  }

  if (thirdSegment.path === 'agent' && fourthSegment?.path) {
    return {
      consumed: segments,
      posParams: {
        company: secondSegment,
        agent_slug: fourthSegment,
      }
    };
  }

  return null;
};

const buildRedirectUrl = (
  path: string,
  queryParams: Record<string, unknown> = {},
  fragment?: string | null
) => {
  const searchParams = new URLSearchParams();

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null) {
          searchParams.append(key, String(item));
        }
      });
      return;
    }

    searchParams.set(key, String(value));
  });

  const queryString = searchParams.toString();
  const fragmentSuffix = fragment ? `#${fragment}` : '';
  const querySuffix = queryString ? `?${queryString}` : '';

  return `${path}${querySuffix}${fragmentSuffix}`;
};

export const routes: Routes = [
  {
    path: 'property',
    component: MainLayoutComponent,
    children: [
      {
        path: 'list',
        loadComponent: () => import('./property-list/property-list.page').then(m => m.PropertyListPage)
      },
      {
        path: 'detail/:id',
        loadComponent: () => import('./property-detail/property-detail.page').then(m => m.PropertyDetailPage)
      }
    ]
  },
  {
    path: 'project',
    component: MainLayoutComponent,
    children: [
      {
        path: 'list',
        pathMatch: 'full',
        redirectTo: ({ queryParams, fragment }) => buildRedirectUrl(`/id/${projectListSlug}`, queryParams, fragment)
      },
      {
        path: 'detail/:id',
        loadComponent: () => import('./project-detail/project-detail.page').then(m => m.ProjectDetailPage)
      }
    ]
  },
  {
    matcher: legacyOfficeRouteMatcher,
    redirectTo: ({ params, queryParams, fragment }) => {
      const company = params['company'];
      const agentSlug = params['agent_slug'];
      const redirectPath = agentSlug
        ? `/id/office/${company}/agent/${agentSlug}`
        : `/id/office/${company}`;

      return buildRedirectUrl(redirectPath, queryParams, fragment);
    },
  },
  {
    matcher: legacyRouteMatcher,
    redirectTo: ({ url, queryParams, fragment }) => {
      const redirectPath = url.map((segment) => segment.path).join('/');
      return buildRedirectUrl(`/id/${redirectPath}`, queryParams, fragment);
    },
  },
  {
    path: ':lang',
    canActivate: [langGuard],
    component: MainLayoutComponent,
    children: [
      { path: '', redirectTo: 'home', pathMatch: 'full' },
      {
        path: 'home',
        loadComponent: () => import('./home/home.page').then((m) => m.HomePage),
      },
      {
        path: 'contactus',
        loadComponent: () => import('./contactus/contactus.page').then(m => m.ContactusPage)
      },
      {
        path: 'about-ljhrealty',
        pathMatch: 'full',
        redirectTo: ({ queryParams, fragment }) => buildRedirectUrl('tentang-kami', queryParams, fragment)
      },
      {
        path: 'article/detail/:title/:id',
        loadComponent: () => import('./article-detail/article-detail.page').then(m => m.ArticleDetailPage)
      },
      {
        path: 'article/:title/:id',
        loadComponent: () => import('./article-detail/article-detail.page').then(m => m.ArticleDetailPage)
      },
      {
        path: 'article',
        pathMatch: 'full',
        redirectTo: ({ queryParams, fragment }) => buildRedirectUrl('info-properti', queryParams, fragment)
      },
      {
        path: 'kpr-simulate',
        pathMatch: 'full',
        redirectTo: ({ queryParams, fragment }) => buildRedirectUrl('simulasi-kpr', queryParams, fragment)
      },
      {
        path: 'outlets',
        pathMatch: 'full',
        redirectTo: ({ queryParams, fragment }) => buildRedirectUrl('daftar-outlet', queryParams, fragment)
      },
      {
        path: 'partnership-opportunity',
        pathMatch: 'full',
        redirectTo: ({ queryParams, fragment }) => buildRedirectUrl('peluang-kemitraan', queryParams, fragment)
      },
      {
        path: 'tentang-kami',
        loadComponent: () => import('./about-ljhrealty/about-ljhrealty.page').then(m => m.AboutLjhrealtyPage)
      },
      {
        path: 'info-properti',
        loadComponent: () => import('./article/article.page').then(m => m.ArticlePage)
      },
      {
        path: 'info-properti/:title',
        loadComponent: () => import('./article-detail/article-detail.page').then(m => m.ArticleDetailPage)
      },
      {
        path: ':title',
        canMatch: [articleRootDetailCanMatch],
        loadComponent: () => import('./article-detail/article-detail.page').then(m => m.ArticleDetailPage)
      },
      {
        path: 'office/:company',
        children: [
          {
            path: '',
            loadComponent: () => import('./home-office/home-office.page').then(m => m.HomeOfficePage)
          },
          {
            path: ':agent_slug',
            pathMatch: 'full',
            redirectTo: ({ params, queryParams, fragment }) => buildRedirectUrl(
              `office/${params['company']}/agent/${params['agent_slug']}`,
              queryParams,
              fragment
            )
          },
          {
            path: 'agent/:agent_slug',
            loadComponent: () => import('./agent-detail/agent-detail.page').then(m => m.AgentDetailPage)
          }
        ]
      },
      {
        path: 'faq',
        loadComponent: () => import('./faq/faq.page').then(m => m.FaqPage)
      },
      {
        path: 'simulasi-kpr',
        loadComponent: () => import('./kpr-simulate/kpr-simulate.page').then(m => m.KprSimulatePage)
      },
      {
        path: 'legal',
        loadComponent: () => import('./legal/legal.page').then(m => m.LegalPage)
      },
      {
        path: 'lidia',
        loadComponent: () => import('./lidia/lidia.page').then(m => m.LidiaPage)
      },
      {
        path: 'market',
        loadComponent: () => import('./market/market.page').then(m => m.MarketPage)
      },
      {
        path: 'daftar-outlet',
        loadComponent: () => import('./outlets/outlets.page').then(m => m.OutletsPage)
      },
      {
        path: 'peluang-kemitraan',
        loadComponent: () => import('./partnership-opportunity/partnership-opportunity.page').then(m => m.PartnershipOpportunityPage)
      },
      {
        path: projectListSlug,
        children: [
          {
            path: '',
            loadComponent: () => import('./project-list/project-list.page').then((m) => m.ProjectListPage),
          },
          {
            path: ':projectSlug/:locationSlug',
            loadComponent: () => import('./project-detail/project-detail.page').then((m) => m.ProjectDetailPage),
          }
        ]
      },
      {
        path: 'property-consultant',
        loadComponent: () => import('./property-consultant/property-consultant.page').then(m => m.PropertyConsultantPage)
      },
      {
        path: ':type',
        canMatch: [propertyTypeCanMatch],
        children: [
          {
            path: '',
            loadComponent: () => import('./property-list/property-list.page').then(m => m.PropertyListPage)
          },
          {
            path: ':location',
            children: [
              {
                path: '',
                loadComponent: () => import('./property-list/property-list.page').then(m => m.PropertyListPage)
              },
              {
                path: ':id',
                loadComponent: () => import('./property-detail/property-detail.page').then(m => m.PropertyDetailPage)
              }
            ]
          }
        ]
      },
      {
        path: '**',
        redirectTo: 'home'
      }
    ]
  },
  {
    path: '',
    redirectTo: 'id',
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: 'id',
  },
];
